import { DropdownMenu, Tooltip } from "radix-ui";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon.tsx";
import { MenuEntries } from "./MenuEntries.tsx";
import { cx } from "./tokens.ts";
import type { MenuEntry, MenuItem } from "./types.ts";

/** Stack.moreActions: the toolbar's trailing "…" menu (and the end of the row's context menu). */
export type MoreActions = { label: string; items: MenuEntry[] };

/** Popovers and menus are portaled: being over one (the emoji picker the row opened) is not "away from the row". */
function inOverlay(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(".b-popover, .b-menu") !== null;
}

/**
 * Web idiom for Stack.hoverActions: a small floating toolbar revealed on hover or keyboard focus. Toggles
 * (`selected` set, e.g. quick reactions) show their state and keep the toolbar open; any other action (open the
 * emoji picker, reply) closes it until the pointer leaves the row. Buttons never take focus from a mouse click,
 * so the composer keeps its caret and no focus ring is left behind. `more` adds a trailing "…" button opening a menu;
 * the toolbar stays shown while that menu is open, and closes like an action once an item is chosen.
 */
export function HoverActions({ items, more }: { items: MenuItem[]; more?: MoreActions }) {
  const ref = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Dismissed until the pointer is over something outside the row (and outside its picker): after a pick it may
  // already be elsewhere, or still over the row, where the toolbar stays closed until it leaves.
  useEffect(() => {
    const group = ref.current?.parentElement;
    if (!dismissed || group === null || group === undefined) return;
    const onOver = (e: PointerEvent) => {
      if (e.target instanceof Node && (group.contains(e.target) || inOverlay(e.target))) return;
      setDismissed(false);
    };
    document.addEventListener("pointerover", onOver);
    return () => document.removeEventListener("pointerover", onOver);
  }, [dismissed]);
  const moreItems = more === undefined ? [] : more.items.map((e) => ("onSelect" in e ? { ...e, onSelect: () => (setDismissed(true), e.onSelect()) } : e));
  return (
    <div ref={ref} className={cx("b-hover-actions", dismissed && !menuOpen && "b-dismissed", menuOpen && "b-open")} role="toolbar">
      {items.map((it) => (
        <Tooltip.Root key={it.id} delayDuration={400}>
          <Tooltip.Trigger asChild>
            <button
              type="button"
              className={cx("b-hover-action", it.icon === undefined && "b-hover-action-text", it.selected === true && "b-active")}
              aria-label={it.label}
              aria-pressed={it.selected}
              data-testid={`hover-action-${it.id}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                if (it.selected === undefined) setDismissed(true);
                it.onSelect();
              }}
            >
              {it.icon === undefined ? it.label : <Icon name={it.icon} size="icon.sm" />}
            </button>
          </Tooltip.Trigger>
          {it.icon === undefined ? null : (
            <Tooltip.Portal>
              <Tooltip.Content className="b-tooltip" side="top" sideOffset={6}>
                {it.label}
              </Tooltip.Content>
            </Tooltip.Portal>
          )}
        </Tooltip.Root>
      ))}
      {more === undefined ? null : (
        <DropdownMenu.Root modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
          <ToolbarTip label={more.label}>
            <DropdownMenu.Trigger asChild>
              <button type="button" className="b-hover-action" aria-label={more.label} data-testid="hover-action-more" onMouseDown={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}>
                <Icon name="more" size="icon.sm" />
              </button>
            </DropdownMenu.Trigger>
          </ToolbarTip>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="b-menu" align="end" sideOffset={6} data-testid="hover-actions-menu">
              <MenuEntries kind="dropdown" entries={moreItems} />
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </div>
  );
}

function ToolbarTip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip.Root delayDuration={400}>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content className="b-tooltip" side="top" sideOffset={6}>
          {label}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
